<template>
	<HiddenHorizontalScrollbar class="jump-link-container">
		<template v-for="(link, index) in links">
			<a
				class="jump-link"
				v-if="!isSectionHidden(link.id)"
				:key="index"
				:href="`#${link.id}`"
				@click.prevent="scrollTo(link.id)"
			>
				{{ link.text }}
			</a>
		</template>
	</HiddenHorizontalScrollbar>
</template>

<script lang="ts" setup>
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { afterNextPaint } from '@/helpers/async-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { MonetizationType } from '@/@types/graphql-types';
import { useMounted } from '@vueuse/core';
import { computed } from 'vue';
import { useExperiment } from '@/libraries/experiment/composables/useExperiment';

const props = withDefaults(
	defineProps<{
		visibleSectionState: Record<string, boolean>;
		container?: HTMLElement | null;
		useListBuybox?: boolean;
	}>(),
	{
		// The container is used to handle the situation of opening a title in a modal (for titles that are missing URLs).
		// Using document on top of the page will result in scrolling the main page instead of the modal.
		container: null,
	}
);

const { t } = useI18n();
const { setBuyboxPresentationType, setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic } = useUserStore();

const { trackEvent: trackCinematicEvent } = useExperiment('EXP_JWTV_TVOD_CINEMATIC_V2');

const isMounted = useMounted();

const links = computed<
	{
		id: string;
		text: string;
		callback?: (parent: HTMLElement | Document) => void;
	}[]
>(() => {
	return [
		{
			id: 'where-to-watch',
			text: t('WEBAPP_WHERE_TO_WATCH'),
			callback: async _ => {
				if (!isMounted.value) {
					return;
				}
				// updating the buybox is expensive.
				// that's why we split it up in 1) optimistic UI update and 2) defer the expensive store update
				setBuyboxFilterTypeOptimistic(BuyBoxFilterOptions.ALL);
				// INP case: artificially give the JS thread time to breathe for upcoming scrolling
				// note: we already have an afterNextPaint in the scrollTo handler, so this is strictly speaking not necessary - but to avoid regression we keep it
				await afterNextPaint();

				setBuyboxPresentationType({ presentationType: null });
				setNewBuyboxFilterType(BuyBoxFilterOptions.ALL);
			},
		},
		{
			id: 'regular-buybox',
			text: t('WEBAPP_WATCH_FOR_FREE'),
			callback: async _ => {
				if (!isMounted.value) {
					return;
				}
				// updating the buybox is expensive.
				// that's why we split it up in 1) optimistic UI update and 2) defer the expensive store update
				setBuyboxFilterTypeOptimistic(BuyBoxFilterOptions.FREE);
				// INP case: artificially give the JS thread time to breathe for upcoming scrolling
				// note: we already have an afterNextPaint in the scrollTo handler, so this is strictly speaking not necessary - but to avoid regression we keep it
				await afterNextPaint();

				setBuyboxPresentationType({ presentationType: MonetizationType.Free });
				setNewBuyboxFilterType(BuyBoxFilterOptions.FREE);
			},
		},
		{
			id: 'episodes-lists',
			text: t('WEBAPP_EPISODES'),
		},
		{
			id: 'synopsis',
			text: t('WEBAPP_SYNOPSIS'),
		},
		{
			id: 'why-to-watch',
			text: t('WEBAPP_WHY_TO_WATCH'),
		},
		{
			id: 'trailers',
			text: t('WEBAPP_TRAILERS_SHORT'),
		},
		{
			id: 'streaming-chart',
			text: t('WEBAPP_STREAMING_CHARTS'),
		},
		{
			id: 'similar-content',
			text: t('WEBAPP_SIMILAR_TITLES'),
		},
	];
});

const isSectionHidden = (id: string) => {
	return props.visibleSectionState[id] === false;
};

async function scrollTo(id: string, disableTracking = false) {
	// INP case: focusing the clicked element is for google enough to consider as "next paint".
	// that's why we split up:
	// 1) focusing the clicked element
	// 2) and calculating where the browser has to move to
	await afterNextPaint();

	/**
	 * We are setting where-to-watch as the parent for regular-buybox because, on SeasonPages and ShowPages,
	 * we have multiple buyboxes shown simultaneously. Doing this ensures that we scroll to the correct buybox.
	 */
	const container = props.container || document;
	const parent = id === 'regular-buybox' ? container.querySelector('#where-to-watch') : container;
	const element = parent?.querySelector(`#${id}`);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
	links.value.find(link => link.id === id)?.callback?.(container);

	if (!disableTracking) {
		const label = id === 'regular-buybox' ? 'watch-for-free' : id;
		const normalizedLabel = label.replaceAll('-', '_');
		TrackingHelper.trackEvent('userinteraction', {
			action: 'jumplink_clicked',
			label: normalizedLabel,
		});
		trackCinematicEvent({
			action: 'click',
			label: 'jumplink_clicked',
			property: normalizedLabel,
			value: props.useListBuybox ? 1 : 0,
		});
	}
}
</script>

<style lang="scss">
@import '~@/styles/mixins.scss';

html {
	scroll-padding-top: 56px;
	@media screen and (max-width: 1050px) {
		scroll-padding-top: 105px;
	}
}
.jump-link-container {
	display: flex;
	align-items: center;
	gap: 16px;
	overflow: visible;
	z-index: 10;

	@include md() {
		width: 210%;
	}
}

.jump-link {
	background-color: #111924;
	border-radius: 20px;
	font-size: 16px;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.015em;
	color: #c6c8cd;
	padding: 12px 20px;
	margin-inline-end: 16px;

	&:hover {
		cursor: pointer;
		background-color: #1b283a;
	}
}
</style>
